import moment from 'moment';
import Delta from 'quill-delta';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { JunkIcon, PencilIcon } from '@treams/treams-icons';
import {
	Comment as CommentType,
	User,
	useArchiveCommentMutation,
	useCommentMutation,
} from '@treams/treams-network';
import {
	Avatar,
	Editable,
	FlexGap,
	IconButton,
	NoPrintWrapper,
	Paragraph,
	Spinner,
	tokens,
} from '@treams/treams-ui';

import { CommentSkeleton } from '.';

type CommentView = Pick<
	CommentType,
	'id' | 'content' | 'createdAt' | 'isArchived' | 'viewerCanUpdate'
> & {
	user: ComponentProps<typeof Avatar>['user'] & Pick<User, 'id'>;
};

type Props = {
	comment: CommentView;
	onDelete?: () => void;
	onRefresh?: () => void;
};

export default function Comment({ comment, onDelete, onRefresh }: Props) {
	const { t } = useTranslation();

	const [isActive] = useState(false);

	const [archive, commentArchiveResponse] = useArchiveCommentMutation({
		variables: {
			input: {
				id: comment.id,
			},
		},
	});

	const [update] = useCommentMutation();

	const commentValue = comment.isArchived
		? new Delta().insert(t('comments.comment.content.removed'))
		: comment.content;

	const onArchiveClick = async () => {
		await archive();
		onDelete?.();
		onRefresh?.();
	};

	const onContentChange = (value: Delta) => {
		update({
			variables: {
				input: {
					id: comment.id,
					content: value,
				},
			},
		});
	};

	return (
		<Editable.Quill.Button
			active={isActive}
			onChange={onContentChange}
			value={commentValue}
			buttonProps={{
				icon: <PencilIcon />,
				type: 'secondary',
			}}
		>
			{(value, { button, active }) => (
				<CommentWrapper alignItems="center">
					<Flex minWidth="100%">
						<Box flexGrow={0} flexShrink={0}>
							<Link to={`/users/${comment.user.id}`}>
								<Avatar user={comment.user} size={32} />
							</Link>
						</Box>
						<Box flexGrow={1} flexShrink={1} ml={2}>
							<Flex mb={2} ml={2}>
								<Paragraph flexGrow={1} flexShrink={1}>
									<Link to={`/users/${comment.user.id}`}>
										{comment.user.firstName} {comment.user.lastName}
									</Link>
								</Paragraph>
								<Timestamp flexGrow={0} flexShrink={0} ml={2}>
									{moment(comment.createdAt).format('LLL')}
								</Timestamp>
							</Flex>
							<Flex alignItems="center" justifyContent="center">
								<Container p={2} flexGrow={1} flexShrink={1} minWidth="100%">
									<Flex alignItems="flex-start">
										<Paragraph flexGrow={1} flexShrink={1}>
											{/* Internally value is put in a Quill-text */}
											{comment.isArchived ? <Box opacity={0.5}>{value}</Box> : value}
										</Paragraph>
										{comment.viewerCanUpdate && !active && (
											<NoPrintWrapper>
												<Flex flexGrow={0} flexShrink={0} ml={2}>
													{commentArchiveResponse.loading ? (
														<Spinner />
													) : (
														<FlexGap gap={tokens.gap.inline}>
															{button}
															<IconButton
																icon={<JunkIcon />}
																type="destructive"
																onClick={() => onArchiveClick()}
																aria-label={t('common.delete')}
															/>
														</FlexGap>
													)}
												</Flex>
											</NoPrintWrapper>
										)}
									</Flex>
								</Container>
							</Flex>
						</Box>
					</Flex>
				</CommentWrapper>
			)}
		</Editable.Quill.Button>
	);
}

const Timestamp = styled(Paragraph)`
	opacity: 0;
	transition: opacity 0.2s ease-in-out;
`;

const CommentWrapper = styled(Flex)`
	&:hover ${Timestamp}, &:focus-within ${Timestamp} {
		opacity: 0.6;
	}
`;

const Container = styled(Box)`
	border-radius: ${tokens.radius.sm}px;
	background: ${tokens.surface.secondary};
`;

Comment.Skeleton = CommentSkeleton;
